/**
 * <TutorResourcesPage> — l'onglet « Ressources pédagogiques » du masteur (page).
 *
 * L'ancien `TutorResources`, sans son référentiel des activités ni le fichier des énoncés (obsolète,
 * retiré le 03/11) : quatre entrées. Deux restent dans l'app — « Les 5 règles d'or » (le texte, dans
 * une feuille : `<TutorGoldenRules>`, à la place de l'affiche en image) et « Formation du tuteur »
 * (« Vidéos coaching », `<TutorCoachingVideosPage>`) ; deux sortent vers une page web, et le disent par
 * leur glyphe : la charte d'engagement, le blog. Les lignes d'une carte.
 *
 * Onglet racine : pas d'en-tête, c'est la coquille qui le pose. La page porte son titre, « Ressources
 * pédagogiques », comme « Offres de cours » le sien (le nom long de l'onglet « Ressources », arbitrage
 * 1 de `parite-tuteur/coquille-accueil.md`) ; la carte des lignes n'a plus d'intitulé à elle.
 *
 * La même page sert en live (le panneau des ressources du tuteur) ; ce qui l'y monte appartient au
 * live, et la feuille des règles s'y ouvre par-dessus, sans quitter la salle.
 *
 * **Dans une feuille** (`inSheet`, le live) : les lignes seules. La coquille d'une page (sa colonne centrée, son
 * fond, son défilement) rognait les lignes à gauche dans la feuille et posait deux bandes grises (live du 04/11) ;
 * le titre est celui de la feuille, il ne se répète pas.
 */
import React from "react"
import { View } from "react-native"

import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Title } from "../../atoms/typography/Title"
import { Modal } from "../../molecules/Modal"
import { SettingsSection, type SettingsSectionRow } from "../../molecules/SettingsSection"
import { TUTOR_GOLDEN_RULES_TITLE, TutorGoldenRules } from "../../organisms/TutorGoldenRules"
import { PageTemplate } from "../../templates/PageTemplate"

export type TutorResourcesPageProps = {
    /** « Les 5 règles d'or » : ouvre leur feuille. */
    onOpenGoldenRules: () => void
    /** La feuille des règles d'or est ouverte. */
    goldenRulesVisible?: boolean
    onCloseGoldenRules: () => void
    /** Le nom du champion qui prépare l'élève, que les règles d'or citent. */
    helperName: string
    onOpenTrainingVideos: () => void
    /** Les deux pages web. */
    onOpenCharter: () => void
    onOpenBlog: () => void
    /** Posée dans une feuille qui porte le titre (le live) : les lignes seules. Défaut `false`. */
    inSheet?: boolean
    testID?: string
}

/** Une colonne de lignes, pas un tableau de bord. */
const RESOURCES_WIDTH = 720

/** Le titre de la page, le nom long de l'onglet « Ressources ». */
const TITLE = "Ressources pédagogiques"

const external = <Icon name="external-link-alt" size={16} color="text-muted" />

export function TutorResourcesPage(props: TutorResourcesPageProps) {
    const {
        onOpenGoldenRules,
        goldenRulesVisible = false,
        onCloseGoldenRules,
        helperName,
        onOpenTrainingVideos,
        onOpenCharter,
        onOpenBlog,
        inSheet = false,
        testID,
    } = props

    const rows: SettingsSectionRow[] = [
        { key: "golden-rules", iconName: "star", label: "Les 5 règles d'or", onPress: onOpenGoldenRules },
        { key: "training", iconName: "play", label: "Formation du tuteur", onPress: onOpenTrainingVideos },
        {
            key: "charter",
            iconName: "handshake",
            label: "La charte d'engagement",
            trailing: external,
            chevron: false,
            onPress: onOpenCharter,
        },
        {
            key: "blog",
            iconName: "book",
            label: "Le Blog de Masteur",
            trailing: external,
            chevron: false,
            onPress: onOpenBlog,
        },
    ]

    const goldenRules = (
        <Modal
            visible={goldenRulesVisible}
            onClose={onCloseGoldenRules}
            title={TUTOR_GOLDEN_RULES_TITLE}
            fullScreenOnSmall
        >
            <TutorGoldenRules helperName={helperName} />
        </Modal>
    )
    if (inSheet) {
        return (
            <View testID={testID}>
                <SettingsSection rows={rows} />
                {goldenRules}
            </View>
        )
    }
    return (
        <PageTemplate testID={testID} padding="4" maxContentWidth={RESOURCES_WIDTH}>
            <Stack gap="4">
                <Title size="text-2xl">{TITLE}</Title>
                <SettingsSection rows={rows} />
            </Stack>
            {goldenRules}
        </PageTemplate>
    )
}
